import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  FolderTree,
  Pencil,
  Plus,
  Star,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import type { FolderTemplate, FolderTemplatePath } from "@/hooks/use-folders";
import {
  useCreateFolderTemplate,
  useCreateFolderTemplatePath,
  useDeleteFolderTemplatePath,
  useFolderTemplatePaths,
  useFolderTemplates,
  useSetDefaultFolderTemplate,
  useUpdateFolderTemplate,
  useUpdateFolderTemplatePath,
} from "@/hooks/use-folders";

const AUTO_POPULATE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "— None —" },
  { value: "commitment", label: "Commitment" },
  { value: "change_order", label: "Change Order" },
  { value: "submittal", label: "Submittal" },
  { value: "rfi", label: "RFI" },
  { value: "pay_application", label: "Pay Application" },
  { value: "daily_log", label: "Daily Log" },
  { value: "bid_package", label: "Bid Package" },
  { value: "punch_list_item", label: "Punch List Item" },
  { value: "project_task", label: "Project Task" },
];

function autoPopulateLabel(value: string | null) {
  if (!value) return null;
  return AUTO_POPULATE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

// ── Template Dialog (create + edit) ──

function TemplateDialog({
  template,
  open,
  onOpenChange,
}: {
  template: FolderTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateFolderTemplate();
  const update = useUpdateFolderTemplate();

  const isEdit = template !== null;
  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [isDefault, setIsDefault] = useState(template?.isDefault ?? false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = { name, description, isDefault };
    if (isEdit && template) {
      update.mutate({ id: template.id, ...payload }, { onSuccess: () => onOpenChange(false) });
    } else {
      create.mutate(payload, { onSuccess: () => onOpenChange(false) });
    }
  }

  const isPending = isEdit ? update.isPending : create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[50vw]">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Folder Template" : "New Folder Template"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ft-name">Name</Label>
            <Input
              id="ft-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g. Standard Construction Project"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ft-desc">Description</Label>
            <Textarea
              id="ft-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description of when this template should be used."
            />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Default Template</Label>
              <p className="text-xs text-muted-foreground">
                New projects use this template's folders by default.
              </p>
            </div>
            <Switch checked={isDefault} onCheckedChange={setIsDefault} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !name.trim()}>
              {isPending ? "Saving..." : isEdit ? "Save Changes" : "Create Template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Path Dialog (create + edit) ──

function PathDialog({
  templateId,
  path,
  open,
  onOpenChange,
  nextDisplayOrder,
}: {
  templateId: string;
  path: FolderTemplatePath | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nextDisplayOrder: number;
}) {
  const create = useCreateFolderTemplatePath(templateId);
  const update = useUpdateFolderTemplatePath(templateId);

  const isEdit = path !== null;
  const [fullPath, setFullPath] = useState(path?.fullPath ?? "");
  const [autoPopulateEntity, setAutoPopulateEntity] = useState(path?.autoPopulateEntity ?? "");
  const [isRequired, setIsRequired] = useState(path?.isRequired ?? false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      fullPath: fullPath.trim(),
      autoPopulateEntity: autoPopulateEntity || null,
      isRequired,
    };
    if (isEdit && path) {
      update.mutate({ pathId: path.id, ...payload }, { onSuccess: () => onOpenChange(false) });
    } else {
      create.mutate(
        { ...payload, displayOrder: nextDisplayOrder },
        { onSuccess: () => onOpenChange(false) },
      );
    }
  }

  const isPending = isEdit ? update.isPending : create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[50vw]">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Folder Path" : "Add Folder Path"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fp-path">Full Path</Label>
            <Input
              id="fp-path"
              value={fullPath}
              onChange={(e) => setFullPath(e.target.value)}
              required
              placeholder="e.g. 03 - Permits & Approvals/Building Permits"
            />
            <p className="text-xs text-muted-foreground">
              Use <code>/</code> to nest subfolders.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fp-auto">Auto-Populate Entity</Label>
            <Select
              value={autoPopulateEntity || "none"}
              onValueChange={(v) => setAutoPopulateEntity(v === "none" ? "" : v)}
            >
              <SelectTrigger id="fp-auto">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUTO_POPULATE_OPTIONS.map((o) => (
                  <SelectItem key={o.value || "none"} value={o.value || "none"}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Attachments on this entity type are filed into this folder automatically.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Required Folder</Label>
              <p className="text-xs text-muted-foreground">
                Marked as required when instantiated on a project.
              </p>
            </div>
            <Switch checked={isRequired} onCheckedChange={setIsRequired} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !fullPath.trim()}>
              {isPending ? "Saving..." : isEdit ? "Save Changes" : "Add Path"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Per-template paths table ──

function TemplatePaths({ templateId }: { templateId: string }) {
  const { data, isLoading, isError, error, refetch } = useFolderTemplatePaths(templateId);
  const deletePath = useDeleteFolderTemplatePath(templateId);
  const [addOpen, setAddOpen] = useState(false);
  const [editingPath, setEditingPath] = useState<FolderTemplatePath | null>(null);
  const [pathToDelete, setPathToDelete] = useState<FolderTemplatePath | null>(null);

  if (isLoading) {
    return <Skeleton className="h-24 w-full" />;
  }

  if (isError) {
    return <ErrorState message={error?.message} onRetry={() => refetch()} />;
  }

  const paths = data ?? [];
  const nextDisplayOrder = paths.reduce((max, p) => Math.max(max, p.displayOrder ?? 0), -1) + 1;

  return (
    <div className="space-y-3 pt-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Folder Paths ({paths.length})
        </p>
        <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
          <Plus className="mr-1 h-3 w-3" />
          Add Path
        </Button>
      </div>

      {paths.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No folder paths yet. Add one to get started.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Full Path</TableHead>
              <TableHead className="w-40">Auto-Populate</TableHead>
              <TableHead className="w-24">Required</TableHead>
              <TableHead className="w-24 text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {paths.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-mono text-xs">{p.fullPath}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {autoPopulateLabel(p.autoPopulateEntity) ?? "--"}
                </TableCell>
                <TableCell>
                  {p.isRequired ? (
                    <Badge variant="secondary" className="text-xs">
                      Required
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setEditingPath(p)}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => setPathToDelete(p)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PathDialog
        templateId={templateId}
        path={null}
        open={addOpen}
        onOpenChange={setAddOpen}
        nextDisplayOrder={nextDisplayOrder}
      />
      {editingPath ? (
        <PathDialog
          templateId={templateId}
          path={editingPath}
          open={true}
          onOpenChange={(o) => {
            if (!o) setEditingPath(null);
          }}
          nextDisplayOrder={nextDisplayOrder}
        />
      ) : null}
      <ConfirmDialog
        open={pathToDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPathToDelete(null);
        }}
        title="Delete folder path?"
        description={`"${pathToDelete?.fullPath ?? ""}" will be removed from this template. Existing projects are not affected.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          if (pathToDelete) {
            deletePath.mutate(pathToDelete.id, { onSuccess: () => setPathToDelete(null) });
          }
        }}
      />
    </div>
  );
}

// ── Template card (expandable) ──

function TemplateCard({
  template,
  onEdit,
}: {
  template: FolderTemplate;
  onEdit: (template: FolderTemplate) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const setDefault = useSetDefaultFolderTemplate();

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <button
            type="button"
            className="flex items-start gap-2 text-left"
            onClick={() => setExpanded((x) => !x)}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 mt-1 text-muted-foreground shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 mt-1 text-muted-foreground shrink-0" />
            )}
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {template.name ?? "Untitled"}
                {template.isDefault ? (
                  <Badge variant="default" className="text-xs">
                    <Star className="mr-1 h-3 w-3" />
                    Default
                  </Badge>
                ) : null}
              </CardTitle>
              {template.description ? (
                <p className="mt-1 text-sm text-muted-foreground">{template.description}</p>
              ) : null}
            </div>
          </button>
          <div className="flex items-center gap-1 shrink-0">
            {!template.isDefault ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDefault.mutate(template.id)}
                disabled={setDefault.isPending}
              >
                <Star className="mr-1 h-3 w-3" />
                Make Default
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(template)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      {expanded ? (
        <CardContent>
          <TemplatePaths templateId={template.id} />
        </CardContent>
      ) : null}
    </Card>
  );
}

// ── Main Page ──

export function FolderTemplatesPage() {
  const { data, isLoading, isError, error, refetch } = useFolderTemplates();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<FolderTemplate | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isError) {
    return <ErrorState message={error?.message} onRetry={() => refetch()} />;
  }

  const templates = data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Folder Templates"
        description="Reusable folder structures applied to new projects"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/config">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Configuration
              </Link>
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Template
            </Button>
          </div>
        }
      />

      {templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FolderTree className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold">No folder templates yet</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Define a folder structure to apply to new construction projects.
            </p>
            <Button className="mt-4" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <TemplateCard key={t.id} template={t} onEdit={setEditingTemplate} />
          ))}
        </div>
      )}

      <TemplateDialog template={null} open={createOpen} onOpenChange={setCreateOpen} />
      {editingTemplate ? (
        <TemplateDialog
          template={editingTemplate}
          open={true}
          onOpenChange={(o) => {
            if (!o) setEditingTemplate(null);
          }}
        />
      ) : null}
    </div>
  );
}
